import { useCallback, useEffect, useMemo, useRef } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Property } from "csstype";

import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DownloadingIcon from "@mui/icons-material/Downloading";
import MovieIcon from "@mui/icons-material/Movie";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import ErrorIcon from "@mui/icons-material/Error";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { playbackDebugSelectors } from "./store/selectors.debug";
import { playbackSelectors } from "./store/selectors.playback";

import { PlayerCard, PlayerCardRef } from "features/videoPlayer/PlayerCard";
import { PlayMode } from "features/videoPlayer/types/PlayMode";

import { selectInWindowMode } from "features/core/store/selectors";

import { NativePlayer } from "features/videoPlayer/NativePlayer";
import { PlayerCardStatus } from "features/videoPlayer/PlayerCardStatus";
import { DownloadPersistedRecordingButton } from "./DownloadPersistedRecordingButton";
import { PersistedRecordingDownloadStatus } from "types/PersistedRecordingDownloadStatus";
import { PlayerRef } from "features/videoPlayer/types/PlayerRef";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { userSelectors } from "features/user/store/selectors";
import { PersistedRecordingWithPlaybackState } from "./types/PersistedRecordingWithPlaybackState";
import { RecordingPlaybackDirection } from "./types/RecordingPlaybackDirection";
import { Stack } from "@mui/material";
import { coreActions } from "features/core/store/slice";
import { caseViewerActions } from "./store/slice";
import { reIdThunks } from "./reId/store/slice";
import { reIdSelectors } from "./reId/store/selectors";
import { ReIdPersistedRecordingButton } from "./reId/ReIdPersistedRecordingButton";
import { ActiveReIdOverlay } from "./reId/ActiveReIdOverlay";
import { DossierCapabilityName } from "types/DossierCapabilityName";

export interface RecordingPlayerProps {
    persistedRecordingId: EntityId;
    originalStartTime: string;
    dossierDeviceId: EntityId;
    dossierDeviceName: string;
    playbackStartDateTime: string | undefined;

    playMode: PlayMode;
    currentPlaybackDirection: RecordingPlaybackDirection;
    direction: RecordingPlaybackDirection;
    onPersistedRecordingReadyForPlayback: () => void;
    onPersistedRecordingWaiting: () => void;
    onPersistedRecordingFailedToLoad: () => void;
    onResetFailedToLoadRecording: () => void;
    onResetFailedToDownloadRecording: () => void;

    recordingWithPlaybackState: PersistedRecordingWithPlaybackState;
    tooltipText: string | undefined;
    hidden: boolean;

    dossierCapabilityNames: DossierCapabilityName[];
}

export function RecordingPlayer(props: RecordingPlayerProps) {
    const dispatch = useAppDispatch();

    const stuckOnBufferingCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const stuckOnBufferingCheck = () => {
        if (stuckOnBufferingCheckTimeoutRef.current) {
            clearTimeout(stuckOnBufferingCheckTimeoutRef.current);
        }

        if (!playerRef.current) {
            return;
        }

        if (playerRef.current?.isReadyToPlay()) {
            console.log(
                "Ready to play using check, seems to be stuck on buffering for no apparent reason",
            );
            onRecordingReadyForPlayback();

            return;
        }

        stuckOnBufferingCheckTimeoutRef.current = setTimeout(() => {
            stuckOnBufferingCheck();
        }, 1000);
    };

    // ReId
    const reIdViewModeIsActive = useAppSelector(
        reIdSelectors.selectReIdViewModeIsActive,
    );

    // debug
    const debugShowBufferingRecordings = useAppSelector(
        playbackDebugSelectors.selectShowBufferingRecordings,
    );
    const debugShowRecordingsOutsidePlaybackAndBufferingWindow = useAppSelector(
        playbackDebugSelectors.selectShowRecordingsOutsidePlaybackAndBufferingWindow,
    );
    const debugShowVideoDirectionInformation = useAppSelector(
        playbackDebugSelectors.selectShowVideoDirectionInformation,
    );

    // get needed store data
    const audioEnabledByDefault = useAppSelector(
        userSelectors.selectAudioEnabledByDefault,
    );
    const selectDeviceRecordingWithPlaybackStateById = useMemo(
        () =>
            playbackSelectors.selectDeviceRecordingWithPlaybackStateByIdFactory(),
        [],
    );
    const recordingWithPlaybackState = useAppSelector((state: RootState) =>
        selectDeviceRecordingWithPlaybackStateById(
            state,
            props.persistedRecordingId,
        ),
    );

    const selectPersistedRecordingFailedToLoadById = useMemo(
        () =>
            playbackSelectors.selectPersistedRecordingFailedToLoadByIdFactory(),
        [],
    );
    const recordingFailedToLoad = useAppSelector((state: RootState) =>
        selectPersistedRecordingFailedToLoadById(
            state,
            props.persistedRecordingId,
        ),
    );
    const playbackSpeed = useAppSelector(playbackSelectors.selectPlaybackSpeed);

    // conditionally start to load stream
    const shouldBeBufferingOrPlaying =
        recordingWithPlaybackState?.inBufferWindow ||
        recordingWithPlaybackState?.inPlaybackWindow;

    const selectCurrentTimeByPersistedRecordingId = useMemo(
        () =>
            playbackSelectors.selectCurrentTimeByPersistedRecordingIdFactory(
                props.direction,
            ),
        [],
    );
    const currentTimeByPersistedRecording = useAppSelector((state: RootState) =>
        selectCurrentTimeByPersistedRecordingId(
            state,
            props.persistedRecordingId,
        ),
    );

    const lastPlaybackStartDateTimeChangedTimestamp = useRef<number>(undefined);

    useEffect(() => {
        if (
            currentTimeByPersistedRecording &&
            (lastPlaybackStartDateTimeChangedTimestamp.current !==
                currentTimeByPersistedRecording.playbackStartDateTimeChangedTimestamp ||
                currentTimeByPersistedRecording.playbackStartDateTimeChangedTimestamp ===
                    undefined)
        ) {
            lastPlaybackStartDateTimeChangedTimestamp.current =
                currentTimeByPersistedRecording.playbackStartDateTimeChangedTimestamp;

            playerRef.current?.videoSetCurrentTime(
                currentTimeByPersistedRecording.currentTime,
            );

            stuckOnBufferingCheck();
        }
    }, [props.playbackStartDateTime]);

    // mark recording as ready
    const onRecordingReadyForPlayback = useCallback(() => {
        if (stuckOnBufferingCheckTimeoutRef.current) {
            clearTimeout(stuckOnBufferingCheckTimeoutRef.current);
        }

        props.onPersistedRecordingReadyForPlayback();
    }, [props.persistedRecordingId]);

    // the player card
    const playerCardRef = useRef<PlayerCardRef>(null);
    const playerRef = useRef<PlayerRef>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const inWindowMode = useAppSelector(selectInWindowMode);

    const playerCardStatusMessageStatusIcon = useMemo(() => {
        if (!recordingWithPlaybackState) return undefined;

        if (recordingFailedToLoad) {
            return <VideocamOffIcon sx={{ fontSize: 150 }} />;
        }

        switch (recordingWithPlaybackState.status) {
            case PersistedRecordingDownloadStatus.Queued:
            case PersistedRecordingDownloadStatus.QueuedForRetry:
            case PersistedRecordingDownloadStatus.QueuedForRetryByUser:
                return <PlaylistAddIcon sx={{ fontSize: 150 }} />;

            case PersistedRecordingDownloadStatus.Scheduled:
                return <ScheduleIcon sx={{ fontSize: 150 }} />;

            case PersistedRecordingDownloadStatus.Downloading:
                return <DownloadingIcon sx={{ fontSize: 150 }} />;

            case PersistedRecordingDownloadStatus.Converting:
                return <MovieIcon sx={{ fontSize: 150 }} />;

            case PersistedRecordingDownloadStatus.Uploading:
                return <CloudUploadIcon sx={{ fontSize: 150 }} />;

            case PersistedRecordingDownloadStatus.Completed:
                return undefined;

            case PersistedRecordingDownloadStatus.Failed:
                return <ErrorIcon sx={{ fontSize: 150 }} />;
        }
    }, [recordingWithPlaybackState]);

    const isFailed =
        recordingWithPlaybackState?.status ===
        PersistedRecordingDownloadStatus.Failed;
    const isCompleted =
        recordingWithPlaybackState?.status ===
        PersistedRecordingDownloadStatus.Completed;
    const isNotFailedOrCompleted = !isFailed && !isCompleted;

    const visible = useMemo(() => {
        const inPlaybackWindow =
            recordingWithPlaybackState?.inPlaybackWindow ?? false;
        const inBufferWindow =
            recordingWithPlaybackState?.inBufferWindow ?? false;

        return (
            (!props.hidden && inPlaybackWindow) ||
            (debugShowBufferingRecordings && inBufferWindow) ||
            (debugShowRecordingsOutsidePlaybackAndBufferingWindow &&
                !inBufferWindow &&
                !inPlaybackWindow)
        );
    }, [props.hidden, recordingWithPlaybackState]);

    let videoMouseOverCursor: Property.Cursor | undefined;
    if (document.fullscreenEnabled) {
        videoMouseOverCursor = inWindowMode ? "zoom-in" : "zoom-out";
    }

    const nativePlayerPlayMode: PlayMode =
        props.currentPlaybackDirection === props.direction
            ? props.playMode
            : "stop";

    const shouldMountReIdOverlay =
        reIdViewModeIsActive &&
        visible &&
        isCompleted &&
        !recordingFailedToLoad;

    const reIdentificationCapabilityEnabled = props.dossierCapabilityNames.some(
        (x) => x === DossierCapabilityName.ReIdentification,
    );

    return (
        <>
            {recordingWithPlaybackState &&
                (shouldBeBufferingOrPlaying ||
                    debugShowRecordingsOutsidePlaybackAndBufferingWindow) && (
                    <Box
                        sx={{
                            display: visible ? "block" : "none",
                        }}
                    >
                        <PlayerCard
                            ref={playerCardRef}
                            sx={{
                                ...(recordingWithPlaybackState.inBufferWindow && {
                                    display: debugShowBufferingRecordings
                                        ? "block"
                                        : "none",
                                    opacity: "75%",
                                    backgroundColor: "orange",
                                }),
                                ...(!recordingWithPlaybackState.inBufferWindow &&
                                    !recordingWithPlaybackState.inPlaybackWindow && {
                                        display:
                                            debugShowRecordingsOutsidePlaybackAndBufferingWindow
                                                ? "block"
                                                : "none",
                                        opacity: "35%",
                                        backgroundColor: "purple",
                                    }),
                            }}
                            cardActions={
                                <>
                                    <Tooltip
                                        title={props.tooltipText}
                                        slotProps={{
                                            popper: {
                                                container:
                                                    playerCardRef.current?.root,
                                            },
                                        }}
                                    >
                                        <AccessTimeIcon
                                            sx={{ marginLeft: 2 }}
                                        />
                                    </Tooltip>
                                    {debugShowVideoDirectionInformation && (
                                        <Tooltip
                                            title={`${props.direction}: ${recordingWithPlaybackState.id}`}
                                            slotProps={{
                                                popper: {
                                                    container:
                                                        playerCardRef.current
                                                            ?.root,
                                                },
                                            }}
                                        >
                                            {props.direction === "forward" ? (
                                                <FastForwardIcon
                                                    sx={{ marginLeft: 2 }}
                                                />
                                            ) : (
                                                <FastRewindIcon
                                                    sx={{ marginLeft: 2 }}
                                                />
                                            )}
                                        </Tooltip>
                                    )}
                                    <Stack
                                        justifyContent={"center"}
                                        direction="row"
                                        spacing={2}
                                        sx={{ flexGrow: 1 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                        >
                                            {props.dossierDeviceName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {
                                                recordingWithPlaybackState
                                                    .recording.recordingId
                                            }
                                        </Typography>
                                    </Stack>

                                    {reIdentificationCapabilityEnabled && (
                                        <ReIdPersistedRecordingButton
                                            reIdViewModeIsActive={
                                                reIdViewModeIsActive
                                            }
                                            persistedRecordingId={
                                                props.persistedRecordingId
                                            }
                                            persistedRecordingState={
                                                recordingWithPlaybackState.status
                                            }
                                            onClick={(isToggledOn: boolean) => {
                                                if (isToggledOn) {
                                                    // send the event to the store to start reId view
                                                    dispatch(
                                                        reIdThunks.reIdViewStartedAsync(
                                                            {
                                                                activeDossierDeviceId:
                                                                    props.dossierDeviceId,
                                                                activePersistedRecordingId:
                                                                    props.persistedRecordingId,
                                                            },
                                                        ),
                                                    );
                                                } else {
                                                    dispatch(
                                                        reIdThunks.reIdViewStopAsync(),
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                    <FeatureFlagGuard
                                        accessibleWithFeatureFlags={[
                                            AppFeature.CaseManagementSingleDownload,
                                        ]}
                                    >
                                        <DownloadPersistedRecordingButton
                                            persistedRecording={
                                                recordingWithPlaybackState
                                            }
                                        />
                                    </FeatureFlagGuard>
                                </>
                            }
                        >
                            {isFailed && (
                                <PlayerCardStatus
                                    mode={"status"}
                                    statusIcon={
                                        <IconButton
                                            onClick={
                                                props.onResetFailedToDownloadRecording
                                            }
                                        >
                                            <RestartAltIcon />
                                        </IconButton>
                                    }
                                    statusMessage={`Failed to download video, click to retry the download`}
                                />
                            )}

                            {isCompleted && (
                                <>
                                    {recordingFailedToLoad && (
                                        <PlayerCardStatus
                                            mode={"status"}
                                            statusIcon={
                                                <IconButton
                                                    onClick={
                                                        props.onResetFailedToLoadRecording
                                                    }
                                                >
                                                    <SyncProblemIcon />
                                                </IconButton>
                                            }
                                            statusMessage={`Failed to load video, click to retry`}
                                        />
                                    )}
                                    {!recordingFailedToLoad && (
                                        <Box
                                            ref={playerContainerRef}
                                            sx={{
                                                position: "relative",
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        >
                                            <NativePlayer
                                                ref={playerRef}
                                                url={`${recordingWithPlaybackState.playbackUrl}?${props.direction}`}
                                                playMode={nativePlayerPlayMode}
                                                playSpeed={Math.abs(
                                                    playbackSpeed,
                                                )}
                                                enableAudio={
                                                    !inWindowMode &&
                                                    audioEnabledByDefault
                                                }
                                                audioEnabledByDefault={
                                                    audioEnabledByDefault
                                                }
                                                onCanPlayThrough={
                                                    onRecordingReadyForPlayback
                                                }
                                                onClickVideo={() => {
                                                    if (inWindowMode) {
                                                        dispatch(
                                                            caseViewerActions.setFullscreenDeviceId(
                                                                {
                                                                    dossierDeviceId:
                                                                        props.dossierDeviceId,
                                                                },
                                                            ),
                                                        );

                                                        dispatch(
                                                            coreActions.setUIMode(
                                                                "fullscreen",
                                                            ),
                                                        );
                                                    } else {
                                                        dispatch(
                                                            caseViewerActions.setFullscreenDeviceId(
                                                                {
                                                                    dossierDeviceId:
                                                                        undefined,
                                                                },
                                                            ),
                                                        );

                                                        dispatch(
                                                            coreActions.setUIMode(
                                                                "window",
                                                            ),
                                                        );
                                                    }
                                                }}
                                                onWaiting={
                                                    props.onPersistedRecordingWaiting
                                                }
                                                videoMouseOverCursor={
                                                    videoMouseOverCursor
                                                }
                                                zoomEnabled={!inWindowMode}
                                                onDurationChange={
                                                    props.onPersistedRecordingWaiting
                                                }
                                                onError={
                                                    props.onPersistedRecordingFailedToLoad
                                                }
                                                enableOverlay={false}
                                            />
                                            {shouldMountReIdOverlay && (
                                                <ActiveReIdOverlay
                                                    persistedRecordingId={
                                                        props.persistedRecordingId
                                                    }
                                                    direction={props.direction}
                                                    containerRef={
                                                        playerContainerRef
                                                    }
                                                />
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}

                            {isNotFailedOrCompleted && (
                                <PlayerCardStatus
                                    mode={"status"}
                                    statusIcon={
                                        playerCardStatusMessageStatusIcon
                                    }
                                    statusMessage={`Current status: ${recordingWithPlaybackState.status}`}
                                />
                            )}
                        </PlayerCard>
                    </Box>
                )}
        </>
    );
}
